/**
 * MobiliCalendar engine bindings for the shared calendar keyboard contract
 * (molecules/Calendar/hotkeys/keyboardSpec.ts) — the provider-registered half.
 *
 * Registered through the central HotkeysProvider under the "termin" scope
 * (route /mobilical maps to it, see HotkeysProvider.getRouteScope), exactly
 * like the old engine's calendarHotkeys — so the "?" help overlay, scope rules
 * and sequence-vs-single-key conflict resolution apply unchanged. The grid's
 * own document listener (useGridKeyboard via MobiliCalendar) owns the raw
 * navigation keys (arrows/ijkl, Enter, Escape, move mode); everything here
 * reads the cursor through the engine's imperative MobiliCalendarKeyboardApi.
 *
 * Shared logic (shelf-selection resolution, "a" cycling, selection
 * continuity) is imported from calendarHotkeys — SINGLE implementation, two
 * engines. Update ./keyboardConformance.ts when bindings are added here.
 */
import { MutableRefObject, useEffect } from "react";
import { useHotkeys as useHotkeysContext } from "../../hotkeys/HotkeysProvider";
import type { Hotkey } from "../../hotkeys/types";
import notificationStore from "../../stores/Notification/notificationStore";
import type CalendarStore from "../../stores/Calendar/calendarStore";
import { PATIENT_SUBTABS } from "../../stores/Patients/patientsStore";
import {
  ShelfData,
  advanceShelfSelectionAfterPlacement,
  cycleShelfSelectionSource,
  durationToMinutes,
  resolveShelfSelectionPayload,
} from "../Calendar/hotkeys/calendarHotkeys";
import { CALENDAR_KEYBOARD_SPEC } from "../Calendar/hotkeys/keyboardSpec";
import { nextDateStr, previousDateStr } from "../Calendar/dateStepping";
import { eventToClientTimeSlot } from "../Calendar/controller/adapters/display-to-client/reverse.adapter";
import type { TemplateHandle } from "../Calendar/DynamicTemplateProvider/types";
import type { MobiliCalendarKeyboardApi } from "./MobiliCalendar";
import type { MCEvent, MCExternalDrop } from "./model/types";

export type MobiliCalendarHotkeyDeps = {
  calendarStore: CalendarStore;
  /** Engine cursor access; null until the grid has mounted. */
  api: () => MobiliCalendarKeyboardApi | null;
  /** All shelf configs (unscheduled + pinboard), including invisible ones. */
  getShelfData: () => ShelfData[];
  /** Template-widget snapshot for the "a" cycling. */
  getTemplateData: () => { visible: boolean; handles: TemplateHandle[] };
  /**
   * The bridge's validated external-drop pipeline (therapist active, payload
   * type, frequency, credentials → controller). True when accepted.
   */
  receiveExternalDrop: (drop: MCExternalDrop) => Promise<boolean>;
  navigateToPatient: (patientId: number, rxId: string | null, subtab: PATIENT_SUBTABS) => void;
  /**
   * Run a registry action (molecules/MobiliCalendar/actions) against the
   * focused event — the hotkey layer and the context menu are two views of
   * the SAME action set. Returns false when the action doesn't apply.
   */
  runAction: (actionId: string, event: MCEvent) => boolean;
};

const slotMinToTimeStr = (slotMin: number): string =>
  `${String(Math.floor(slotMin / 60)).padStart(2, "0")}:${String(slotMin % 60).padStart(2, "0")}`;

/**
 * Build the MCExternalDrop for a payload at the engine's focused cell — the
 * Mobili counterpart of the old engine's planPayloadAtCursor (which needs a
 * FullCalendar api). Returns null (with user feedback) when no cell/duration.
 */
export const dropForPayloadAtCursor = (
  deps: MobiliCalendarHotkeyDeps,
  payload: Record<string, any>
): MCExternalDrop | null => {
  const cell = deps.api()?.getFocusedCell();
  if (!cell) {
    notificationStore.showMessage(
      "Keine Zelle ausgewählt — mit den Pfeiltasten eine Kalenderzelle wählen.",
      "info"
    );
    return null;
  }
  const minutes = durationToMinutes(payload.duration);
  if (!minutes) {
    notificationStore.showMessage("Termin hat keine gültige Dauer.", "warning");
    return null;
  }
  const dateStr = deps.calendarStore.selectedDateStr;
  const start = new Date(`${dateStr}T${slotMinToTimeStr(cell.slotMin)}:00`);
  const end = new Date(start.getTime() + minutes * 60_000);
  return { payload: payload as MCExternalDrop["payload"], resourceId: cell.resourceId, start, end };
};

/** "Belegen" (b) via the Mobili engine — spec: belegen. */
const insertShelfSelectionAtCursor = async (deps: MobiliCalendarHotkeyDeps): Promise<void> => {
  const store = deps.calendarStore;
  const resolved = resolveShelfSelectionPayload(deps);
  if (!resolved) {
    // Stale selection (item vanished from the shelf, e.g. scheduled elsewhere).
    store.setSelectedShelfAppointment(null, null);
    notificationStore.showMessage("Keine Auswahl im Regal.", "info");
    return;
  }
  const drop = dropForPayloadAtCursor(deps, resolved.payload);
  if (!drop) return;
  const accepted = await deps.receiveExternalDrop(drop);
  if (!accepted) return; // pipeline already notified
  advanceShelfSelectionAfterPlacement(store, resolved);
};

const buildMobiliCalendarHotkeys = (
  depsRef: MutableRefObject<MobiliCalendarHotkeyDeps>
): Hotkey[] => {
  const deps = () => depsRef.current;
  const hasShelfSelection = () => {
    const store = depsRef.current?.calendarStore;
    return !!(store?.selectedShelfTemplate || store?.selectedShelfAppointmentId);
  };
  const cursorSlot = () => {
    const event = depsRef.current?.api()?.getFocusedEvent();
    return event?.raw ? eventToClientTimeSlot(event.raw) : null;
  };

  return [
    {
      combo: "b",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.belegen.description,
      handler: () => {
        // With a shelf selection: plan it at the cursor. Without one, the
        // spec's fallback — edit the appointment under the cursor (registry
        // action; dialog is hosted shell-wide in CalendarActionDialogs).
        if (hasShelfSelection()) {
          void insertShelfSelectionAtCursor(deps());
          return;
        }
        const event = deps().api()?.getFocusedEvent();
        if (event) deps().runAction("editAppointment", event);
      },
    },
    {
      combo: "a",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.cycleShelfSelection.description,
      handler: () => cycleShelfSelectionSource(deps()),
    },
    {
      combo: "q",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.togglePatientTab.description,
      handler: () => deps().calendarStore.toggleSelectedPatient(),
    },
    {
      combo: "n",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openNotes.description,
      handler: () => {
        const appointmentId = deps().api()?.getFocusedEvent()?.appointmentId;
        if (appointmentId) deps().calendarStore.openNotesDialog(appointmentId);
      },
    },
    {
      combo: "r",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openPrescriptions.description,
      handler: () => {
        const slot = cursorSlot();
        const patientId = slot?.patient?.id;
        if (patientId) {
          deps().navigateToPatient(patientId, slot?.rx?.id ?? null, PATIENT_SUBTABS.PRESCRIPTIONS);
        }
      },
    },
    {
      combo: "p",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openPatientAppointments.description,
      handler: () => {
        const patientId = cursorSlot()?.patient?.id;
        if (patientId) {
          deps().navigateToPatient(patientId, null, PATIENT_SUBTABS.APPOINTMENTS);
        }
      },
    },
    // ── Registry-backed actions (same objects the context menu renders) ────
    {
      combo: "w",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.markAttended.description,
      handler: () => {
        const event = deps().api()?.getFocusedEvent();
        if (event) deps().runAction("toggleAttended", event);
      },
    },
    {
      combo: "ü",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.openOverview.description,
      handler: () => {
        const event = deps().api()?.getFocusedEvent();
        if (event) deps().runAction("openAppointmentOverview", event);
      },
    },
    {
      combo: "x",
      scope: "termin",
      priority: 150,
      description: CALENDAR_KEYBOARD_SPEC.cancelAppointment.description,
      handler: () => {
        const event = deps().api()?.getFocusedEvent();
        if (event) deps().runAction("cancelAppointment", event);
      },
    },
    {
      combo: "J",
      scope: "termin",
      priority: 150,
      description: `${CALENDAR_KEYBOARD_SPEC.datePage.description} — zurück`,
      handler: () => {
        const store = deps().calendarStore;
        store.setSelectedDateStr(previousDateStr(store.selectedDateStr));
      },
    },
    {
      combo: "L",
      scope: "termin",
      priority: 150,
      description: `${CALENDAR_KEYBOARD_SPEC.datePage.description} — vor`,
      handler: () => {
        const store = deps().calendarStore;
        store.setSelectedDateStr(nextDateStr(store.selectedDateStr));
      },
    },
  ];
};

/** Register the Mobili binding table once; handlers stay fresh via depsRef. */
export const useMobiliCalendarHotkeys = (
  depsRef: MutableRefObject<MobiliCalendarHotkeyDeps>
): void => {
  const { register } = useHotkeysContext();
  useEffect(() => {
    const registrations = buildMobiliCalendarHotkeys(depsRef).map(register);
    return () => registrations.forEach((registration) => registration.unregister());
  }, [register, depsRef]);
};
